import { defineStore } from 'pinia'
import api from '@/utils/api'
import { clearAuthSession, getStoredExpiresAt, getStoredToken, getStoredUser, isStoredSessionExpired, storeAuthSession } from '@/utils/authStorage'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: getStoredToken(),
    user: getStoredUser(),
    expiresAt: getStoredExpiresAt(),
    initialized: false,
    loading: false,
    error: null
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.token && state.user),
    displayName: (state) => state.user?.display_name || state.user?.email || 'Backoffice user'
  },

  actions: {
    persistSession(token, user, expiresAt = null) {
      this.token = token
      this.user = user
      this.expiresAt = expiresAt
      storeAuthSession({ token, user, expiresAt })
    },

    clearSession() {
      this.token = ''
      this.user = null
      this.expiresAt = null
      clearAuthSession()
    },

    async initialize() {
      if (this.initialized) {
        return
      }

      if (!this.token) {
        this.initialized = true
        return
      }

      if (isStoredSessionExpired(this.expiresAt)) {
        this.clearSession()
        this.initialized = true
        return
      }

      try {
        const response = await api.get('/api/endpoints/auth-session.php')
        if (!response.data?.success || !response.data?.data?.user) {
          throw new Error(response.data?.error || 'Unable to restore the authenticated session.')
        }

        this.persistSession(this.token, response.data.data.user, response.data.data.token?.expires_at || this.expiresAt)
      } catch (error) {
        this.clearSession()
      } finally {
        this.initialized = true
      }
    },

    async login(email, password) {
      this.loading = true
      this.error = null

      try {
        const response = await api.post('/api/endpoints/auth-login.php', { email, password })
        if (!response.data?.success || !response.data?.data?.token || !response.data?.data?.user) {
          throw new Error(response.data?.error || 'Unable to sign in.')
        }

        this.persistSession(response.data.data.token, response.data.data.user, response.data.data.expires_at || null)
        this.initialized = true

        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.error || error.message || 'Unable to sign in.'
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      this.loading = true

      try {
        if (this.token) {
          await api.delete('/api/endpoints/auth-logout.php')
        }
      } catch (error) {
        console.warn('Logout request failed:', error)
      } finally {
        this.clearSession()
        this.loading = false
        this.initialized = true
      }
    }
  }
})
